// Main-thread orchestration for the audio lab: owns the AudioContext, the mic stream, the capture
// worklet, the analysis worker, guide-tone playback and the measurement protocols. The page is a
// thin view over this class so the same engine can later drive Learn's Range Finder and Pitch Match.
//
// Guide bus (issue #3708), as in Learn (learn_audio_session.ts, learn_mic_capture.ts): every sound
// the engine plays - the guides, the piano series, the metronome, the round-trip clicks - goes
// through one unity-gain bus, made with the context, and only the bus reaches the speakers. While
// the mic is open the bus also reaches the capture worklet's second input through a mono tap, so
// recordings carry the exact guide audio beside the microphone, sample for sample, as a second
// channel. Stopping the mic takes only the tap away. The worklet ignores that input for everything
// but recording, so the levels, timings, click detection and stats measure what they always did.
//
//   MediaStreamAudioSourceNode ─────────▶ input 0 ┐
//                                                 ├ AudioWorkletNode (syp-capture) ─▶ GainNode (gain 0) ─▶ destination
//   guide bus ─▶ GainNode (mono tap) ───▶ input 1 ┘
//       └─▶ destination
//
// The own-canceller mic option (issue #3708) runs the app's own echo canceller on the lab's capture
// exactly as the Learn mic capture runs it on Android (learn_mic_capture.ts), so the lab measures the
// path that ships: the worker's config carries the canceller before the worker gets its port, and
// once the port is handed over and the guide tap is connected the worklet is told to stream the guide
// reference to the worker; stopping the mic tells it to stop. Like Learn's, the lab's canceller gets
// no delay hint and searches the whole delay range itself: a hint narrows the search to 150 ms either
// side, and one A16 page session put the guide into the microphone 1.07 s after it left the page
// against a 412 ms click round trip, so a hinted canceller would never have locked and the lab would
// have measured the raw path. The click round trip stays an independent check of the delay the
// canceller reports. Learn starts a worker per open, so the canceller starts cold every
// time; the lab's worker outlives a stop, so a stop after a run with the canceller ends the worker
// too and the next start gets a cold one. With the option off nothing is streamed and the worker
// runs no canceller: every protocol measures what it always did.

import { sleep } from '../../../../../../common/src/sleep.ts';
import {
  applyAudioSessionType,
  AudioSessionType,
  createSilentAudioElement,
  readAudioSessionType,
} from '../../../../audio/audio_session.ts';
import captureWorkletUrl from '../../../../audio/capture_worklet.ts?worker&url';
import {
  captureNodeOptions,
  connectReference,
  createGuideBus,
  joinRecordedChunks,
  RecordedChunk,
} from '../../../../audio/guide_reference.ts';
import {
  centsBetween,
  frequencyToMidi,
  median,
  midiToFrequency,
  midiToNoteName,
  toDbfs,
} from '../../../../audio/pitch/notes.ts';
import {
  captureProcessorName,
  defaultDetectorConfig,
  DetectorConfig,
  FrameResult,
  MainToWorkerMessage,
  MainToWorkletMessage,
  WorkerToMainMessage,
  WorkletToMainMessage,
} from '../../../../audio/pitch/types.ts';
import { encodeWav } from '../../../../audio/pitch/wav.ts';
import { ActiveNote, loadInstrument, playNote, stopNote } from '../../../../player/instrument.ts';
import { echoSummary } from './lab_canceller.ts';
import {
  doubleTalkRecordingLabel,
  doubleTalkRecordingMetadata,
  LabRecording,
  recordedRunVerdict,
  recordingMetadata,
} from './lab_recording.ts';

export type GuideKind = 'sine' | 'triangle' | 'piano';

export interface GuideOptions {
  kind: GuideKind;
  midi: number;
  gain: number; // 0..1
}

export interface MicOptions {
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  // iOS is known to change the hardware sample rate when the mic opens; this flag lets us create
  // the AudioContext after getUserMedia instead of before, to compare the two orders
  contextAfterMic: boolean;
  // The app's own echo canceller ahead of the detectors, as Learn runs it on Android (see the header)
  ownCanceller: boolean;
}

export interface LabSessionEntry {
  seq: number;
  at: string;
  audioTime?: number;
  kind: string;
  data?: unknown;
}

export interface LabSession {
  id: string;
  startedAt: string;
  userAgent: string;
  host: string;
  entries: LabSessionEntry[];
}

export interface RoundTripResult {
  samplesMs: number[];
  medianMs?: number;
  threshold: number;
  timeouts: number;
}

export interface LeakStimulus extends GuideOptions {
  label: string;
}

// Compact per-frame trace kept with test results so runs can be analysed offline
export type FrameTrace = Array<{
  t: number; // audio clock seconds
  db: number; // dBFS
  yin?: [number, number]; // Hz, clarity
  mpm?: [number, number];
  target?: [number, number]; // cents, clarity
}>;

export function traceFrames(frames: FrameResult[]): FrameTrace {
  const round = (value: number, digits: number) => Number(value.toFixed(digits));
  return frames.map((frame) => ({
    t: round(frame.time, 3),
    db: Number.isFinite(frame.dbfs) ? round(frame.dbfs, 1) : -999,
    yin: frame.yin ? [round(frame.yin.frequency, 1), round(frame.yin.clarity, 2)] : undefined,
    mpm: frame.mpm ? [round(frame.mpm.frequency, 1), round(frame.mpm.clarity, 2)] : undefined,
    target: frame.target ? [round(frame.target.cents, 1), round(frame.target.clarity, 2)] : undefined,
  }));
}

export interface LeakResult {
  label: string;
  frequency: number;
  noiseDbfs: number;
  levelDbfs: number;
  leakDb: number; // how far the stimulus rises above the noise floor in the mic signal
  yinLockRate: number; // fraction of frames where YIN reported the stimulus pitch
  targetClarity?: number; // median normalised correlation at the stimulus period
  frames: number;
  trace?: FrameTrace;
}

export interface DoubleTalkPhase {
  label: string;
  guideMidi?: number;
  // audio clock, seconds: the phase from its instruction to the next, and the window scored below
  startSec?: number;
  endSec?: number;
  scoredFromSec?: number;
  scoredToSec?: number;
  frames: number;
  voicedRate: number;
  onSingerRate?: number;
  onGuideRate?: number;
  // known-target estimator aimed at the singer's own note during the guide phases
  targetOnSingerRate?: number; // frames within 50 cents of the singer with clarity >= 0.4
  targetClarityMedian?: number;
  // the own canceller's medians over the scored window (lab_canceller.ts), when it ran
  micDbfsMedian?: number;
  echoDbfsMedian?: number;
  residualDbfsMedian?: number;
  delayMsMedian?: number;
  cancelledRate?: number;
  trace?: FrameTrace;
}

export interface DoubleTalkResult {
  singerHz?: number;
  singerMidi?: number;
  otherMidi?: number;
  phases: DoubleTalkPhase[];
  aborted?: string;
  recording?: string; // where a recorded run's WAV landed on the dev server
  recordingError?: string; // why a recorded run's upload failed
}

// What runDoubleTalk records when asked: the label is the page's record label (may be empty)
export interface DoubleTalkRecordOptions {
  label: string;
}

// How `stop()` tears capture down. Measured on iPhone 12 mini, iOS 26.6 (2026-09-04): WebKit
// plays the guide loud through the speaker while capturing, but if the page closes its AudioContext
// AND ends the mic track, the next capture in the same process is quiet for good. Keeping either
// alive (a suspended context is enough) holds the loud configuration. Production rule: one
// AudioContext per page, never closed; mic tracks may come and go.
export type StopMode = 'closeAll' | 'keepContext' | 'keepContextSuspended' | 'keepTrack' | 'keepAll';

const micStoppedReason = 'the mic stopped during the run';

export const voicedClarity = 0.6;
export const voicedDbfs = -50;
const resultHistory = 4000; // frames kept for measurements (~40 s at 94 frames/s)

export class AudioLabEngine {
  audioContext: AudioContext | undefined;
  micOptions: MicOptions | undefined;
  detectorConfig: DetectorConfig = { ...defaultDetectorConfig };
  trackSettings: MediaTrackSettings | undefined;
  latestResult: FrameResult | undefined;
  resultCount = 0;
  lastResultLatencyMs = 0; // audio clock at receipt minus audio clock at frame end
  workletStats: { framesEmitted: number; framesDropped: number; quantaProcessed: number } | undefined;
  recording = false;
  session: LabSession;
  onResult: ((result: FrameResult) => void) | undefined;
  onLog: ((entry: LabSessionEntry) => void) | undefined;

  private stream: MediaStream | undefined;
  private sourceNode: MediaStreamAudioSourceNode | undefined;
  private workletNode: AudioWorkletNode | undefined;
  private silentGain: GainNode | undefined;
  private guideBus: GainNode | undefined; // made with the context; every sound goes through it (see the header)
  private disconnectReference: (() => void) | undefined; // takes the guide bus's tap into the worklet away
  private captureGeneration = 0; // bumped by every stop(), so a run can tell that its mic went away
  private worker: Worker | undefined;
  private workerReady = false;
  private streamingReference = false; // the own canceller runs on this mic (see the header)
  private results: FrameResult[] = [];
  private clickResolver: ((time: number) => void) | undefined;
  private lastClick: { floorRms: number; quantumRms: number; peak: number } | undefined;
  private statsTimer: ReturnType<typeof setInterval> | undefined;
  private recordChunks: RecordedChunk[] = [];
  private activeGuide: { stop: (when: number) => void; options: GuideOptions } | undefined;
  private guideTimer: ReturnType<typeof setTimeout> | undefined;
  private workletContext: AudioContext | undefined; // context the capture worklet module was added to
  private keptContext: AudioContext | undefined; // context deliberately kept open across a stop
  stopMode: StopMode = 'keepContextSuspended';
  // audio session type to request before the AudioContext is created (undefined = leave alone)
  audioSessionTypeAtCreation: AudioSessionType | undefined;
  private keepAliveElement: HTMLAudioElement | undefined;
  private scheduledSources: Array<{ stop: (when: number) => void }> = [];
  private backgroundLoggingInstalled = false;

  constructor() {
    this.session = {
      id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      startedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      host: window.location.host,
      entries: [],
    };
  }

  get running(): boolean {
    return Boolean(this.workletNode);
  }

  get activeGuideOptions(): GuideOptions | undefined {
    return this.activeGuide?.options;
  }

  log(kind: string, data?: unknown) {
    const entry: LabSessionEntry = {
      seq: this.session.entries.length,
      at: new Date().toISOString(),
      audioTime: this.audioContext?.currentTime,
      kind,
      data,
    };
    this.session.entries.push(entry);
    this.onLog?.(entry);
  }

  getContextInfo() {
    const context = this.audioContext;
    if (!context) {
      return undefined;
    }
    return {
      state: context.state,
      sampleRate: context.sampleRate,
      baseLatencyMs: context.baseLatency * 1000,
      // outputLatency is missing on some browsers
      outputLatencyMs: typeof context.outputLatency === 'number' ? context.outputLatency * 1000 : undefined,
      audioSession: readAudioSessionType(),
      visibility: document.visibilityState,
    };
  }

  private ensureContext(): AudioContext {
    if (!this.audioContext) {
      if (this.audioSessionTypeAtCreation) {
        this.log('audioSession.beforeCreate', {
          requested: this.audioSessionTypeAtCreation,
          result: applyAudioSessionType(this.audioSessionTypeAtCreation),
        });
      }
      // Deliberately not createAudioContext(), so outside the registry a heal of the page's frozen
      // clock suspends (live_audio_contexts.ts, issue #4116): this context captures the microphone,
      // and a heal's suspend would cut a recording short. While it runs, a heal cannot stop the
      // output unit every context shares; the lab is engineering-only, and should its output die, a
      // reload is its remedy.
      this.audioContext = new AudioContext({ latencyHint: 'interactive' });
      this.audioContext.addEventListener('statechange', () => this.log('context.state', this.audioContext?.state));
      this.installBackgroundLogging();
      this.log('context.created', this.getContextInfo());
    }
    // Made after the context is kept, so a throw here costs no context: the next call makes the bus
    if (!this.guideBus) {
      this.guideBus = createGuideBus(this.audioContext);
    }
    return this.audioContext;
  }

  // Where every sound connects: the guide bus, never the destination itself
  private guideOutput(): GainNode {
    this.ensureContext();
    return this.guideBus!;
  }

  // The one way the engine plays a soundfont note: through the guide bus, so the note is in the
  // reference the worklet records. playNote alone would default to the speakers.
  private playPianoNote(midi: number, gain: number, when: number): ActiveNote {
    return playNote(this.ensureContext(), midiToNoteName(midi), gain, when, this.guideOutput());
  }

  // Timestamped trail of what the page and the context do while the phone is locked or the app is
  // in the background: the question the silent-switch and background-playback experiments ask
  private installBackgroundLogging() {
    if (this.backgroundLoggingInstalled) {
      return;
    }
    this.backgroundLoggingInstalled = true;
    document.addEventListener('visibilitychange', () =>
      this.log('visibility', { state: document.visibilityState, context: this.audioContext?.state }),
    );
    window.addEventListener('pagehide', () => this.log('pagehide', { context: this.audioContext?.state }));
    window.addEventListener('pageshow', () => this.log('pageshow', { context: this.audioContext?.state }));
  }

  // ---- audio session experiments (iOS) ---------------------------------------------------------

  setAudioSessionType(type: AudioSessionType) {
    this.log('audioSession.set', {
      requested: type,
      result: applyAudioSessionType(type),
      contextState: this.audioContext?.state,
    });
  }

  // A looping silent <audio> element; on WebKit this alone moves the page to the playback category
  async startKeepAlive(): Promise<void> {
    if (!this.keepAliveElement) {
      this.keepAliveElement = createSilentAudioElement();
    }
    try {
      await this.keepAliveElement.play();
      this.log('keepAlive.started', { paused: this.keepAliveElement.paused });
    } catch (error) {
      this.log('keepAlive.error', String(error));
      throw error;
    }
  }

  stopKeepAlive() {
    this.keepAliveElement?.pause();
    this.log('keepAlive.stopped');
  }

  // Piano notes scheduled ahead on the audio clock, no timers involved, so that whether they keep
  // sounding with the screen locked says something about the audio graph alone
  async playPianoSeries(count: number, intervalSec: number, midi: number, gain: number): Promise<void> {
    const context = this.ensureContext();
    await context.resume();
    await new Promise<void>((resolve) => loadInstrument(() => resolve()));
    const start = context.currentTime + 0.1;
    for (let i = 0; i < count; i += 1) {
      const note = this.playPianoNote(midi, gain, start + i * intervalSec);
      this.scheduledSources.push({ stop: (at) => stopNote(note, at) });
    }
    this.log('series.piano', {
      count,
      intervalSec,
      midi,
      gain,
      seconds: count * intervalSec,
      audioSession: readAudioSessionType(),
    });
  }

  // A metronome of short 1 kHz clicks, all scheduled up front on the audio clock
  async playMetronome(bpm: number, seconds: number): Promise<void> {
    const context = this.ensureContext();
    await context.resume();
    const start = context.currentTime + 0.1;
    const beats = Math.floor((seconds * bpm) / 60);
    for (let i = 0; i < beats; i += 1) {
      const at = start + (i * 60) / bpm;
      const oscillator = context.createOscillator();
      oscillator.frequency.value = 1000;
      const gain = context.createGain();
      gain.gain.setValueAtTime(0.6, at);
      gain.gain.setValueAtTime(0, at + 0.012);
      oscillator.connect(gain);
      gain.connect(this.guideOutput());
      oscillator.start(at);
      oscillator.stop(at + 0.02);
      this.scheduledSources.push({ stop: (when) => oscillator.stop(when) });
    }
    this.log('series.metronome', { bpm, seconds, beats, audioSession: readAudioSessionType() });
  }

  stopScheduled() {
    const at = this.audioContext?.currentTime ?? 0;
    for (const source of this.scheduledSources) {
      try {
        source.stop(at);
      } catch {
        // already ended
      }
    }
    this.scheduledSources = [];
    this.log('series.stopped');
  }

  private ensureWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL('../../../../audio/analysis_worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (event: MessageEvent<WorkerToMainMessage>) => this.onWorkerMessage(event.data);
      this.worker.onerror = (event) => this.log('worker.error', event.message);
    }
    return this.worker;
  }

  private endWorker() {
    if (!this.worker) {
      return;
    }
    this.worker.onmessage = null;
    this.worker.onerror = null;
    this.worker.terminate();
    this.worker = undefined;
    this.workerReady = false;
  }

  private postToWorker(message: MainToWorkerMessage, transfer?: Transferable[]) {
    this.ensureWorker().postMessage(message, transfer ?? []);
  }

  private postToWorklet(message: MainToWorkletMessage, transfer?: Transferable[]) {
    this.workletNode?.port.postMessage(message, transfer ?? []);
  }

  private onWorkerMessage(message: WorkerToMainMessage) {
    if (message.type === 'ready') {
      this.workerReady = true;
      return;
    }
    const { result } = message;
    this.latestResult = result;
    this.resultCount += 1;
    if (this.audioContext) {
      this.lastResultLatencyMs = (this.audioContext.currentTime - result.time) * 1000;
      result.mainThreadLatencyMs = this.lastResultLatencyMs;
    }
    this.results.push(result);
    if (this.results.length > resultHistory) {
      this.results.splice(0, this.results.length - resultHistory);
    }
    this.onResult?.(result);
  }

  private onWorkletMessage(message: WorkletToMainMessage) {
    switch (message.type) {
      case 'click':
        this.lastClick = { floorRms: message.floorRms, quantumRms: message.quantumRms, peak: message.peak };
        this.clickResolver?.(message.time);
        this.clickResolver = undefined;
        break;
      case 'recordChunk':
        this.recordChunks.push({ samples: message.samples, reference: message.reference, time: message.time });
        break;
      case 'stats':
        this.workletStats = {
          framesEmitted: message.framesEmitted,
          framesDropped: message.framesDropped,
          quantaProcessed: message.quantaProcessed,
        };
        break;
    }
  }

  async start(mic: MicOptions, detector?: Partial<DetectorConfig>) {
    if (this.workletNode) {
      await this.stop();
    }
    this.micOptions = mic;
    this.ensureWorker();
    let context = mic.contextAfterMic ? undefined : this.ensureContext();
    if (context) {
      await context.resume();
      this.log('context.beforeMic', { ...this.getContextInfo(), reused: context === this.keptContext });
    }

    const constraints: MediaStreamConstraints = {
      audio: {
        echoCancellation: mic.echoCancellation,
        noiseSuppression: mic.noiseSuppression,
        autoGainControl: mic.autoGainControl,
      },
      video: false,
    };
    const requestedAt = performance.now();
    const keptTrack = this.stream?.getAudioTracks()[0];
    const reusingTrack = Boolean(keptTrack && keptTrack.readyState === 'live');
    if (keptTrack && !reusingTrack) {
      this.stream = undefined;
    }
    if (!reusingTrack) {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (error) {
        this.log('mic.error', { name: (error as Error).name, message: (error as Error).message });
        throw error;
      }
    }
    const track = this.stream!.getAudioTracks()[0];
    track.enabled = true;
    this.trackSettings = track.getSettings();
    this.log('mic.opened', {
      reusedTrack: reusingTrack,
      elapsedMs: performance.now() - requestedAt,
      requested: constraints.audio,
      settings: this.trackSettings,
      label: track.label,
      capabilities: typeof track.getCapabilities === 'function' ? track.getCapabilities() : undefined,
    });
    track.onended = () => this.log('mic.trackEnded');

    context = this.ensureContext();
    await context.resume();
    this.log('context.afterMic', { ...this.getContextInfo(), reused: context === this.keptContext });

    if (this.workletContext !== context) {
      await context.audioWorklet.addModule(captureWorkletUrl);
      this.workletContext = context;
    }
    // Undefined rather than absent when the option is off: the worker merges configs, and an earlier
    // start's canceller must not carry over
    const canceller = mic.ownCanceller ? { enabled: true } : undefined;
    this.detectorConfig = { ...this.detectorConfig, ...detector, sampleRate: context.sampleRate, canceller };
    this.postToWorker({ type: 'config', config: this.detectorConfig });

    this.sourceNode = context.createMediaStreamSource(this.stream!);
    this.workletNode = new AudioWorkletNode(
      context,
      captureProcessorName,
      captureNodeOptions(this.detectorConfig.windowSize, this.detectorConfig.hopSize),
    );
    this.workletNode.port.onmessage = (event: MessageEvent<WorkletToMainMessage>) => this.onWorkletMessage(event.data);
    const channel = new MessageChannel();
    this.postToWorker({ type: 'port', port: channel.port1 }, [channel.port1]);
    this.postToWorklet({ type: 'port', port: channel.port2 }, [channel.port2]);

    // Safari only pulls audio through a worklet that reaches the destination; keep it inaudible
    this.silentGain = context.createGain();
    this.silentGain.gain.value = 0;
    this.sourceNode.connect(this.workletNode);
    this.workletNode.connect(this.silentGain);
    this.silentGain.connect(context.destination);
    this.disconnectReference = connectReference(context, this.guideOutput(), this.workletNode);
    if (mic.ownCanceller) {
      this.postToWorklet({ type: 'reference', enabled: true });
      this.streamingReference = true;
    }
    this.results = [];
    this.resultCount = 0;
    this.log('capture.started', { detector: this.detectorConfig, workerReady: this.workerReady });
    this.statsTimer = setInterval(() => this.log('stats', this.recentStats(5)), 5000);
  }

  async stop() {
    this.captureGeneration += 1;
    this.stopGuide();
    if (this.statsTimer) {
      clearInterval(this.statsTimer);
      this.statsTimer = undefined;
      this.log('stats', this.recentStats(5));
    }
    if (this.recording) {
      await this.stopRecording();
    }
    const mode = this.stopMode;
    const keepTrack = mode === 'keepTrack' || mode === 'keepAll';
    const keepContext = mode === 'keepContext' || mode === 'keepContextSuspended' || mode === 'keepAll';
    if (keepTrack) {
      this.stream?.getTracks().forEach((track) => {
        track.enabled = false;
      });
    } else {
      this.stream?.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
    }
    this.sourceNode?.disconnect();
    if (this.streamingReference) {
      this.postToWorklet({ type: 'reference', enabled: false });
    }
    this.workletNode?.disconnect();
    this.silentGain?.disconnect();
    // Only the tap: the bus stays on the speakers, so a guide plays on without the mic
    this.disconnectReference?.();
    this.disconnectReference = undefined;
    if (this.streamingReference) {
      this.endWorker(); // the next start's canceller starts cold, as Learn's does (see the header)
      this.streamingReference = false;
    }
    this.sourceNode = undefined;
    this.workletNode = undefined;
    this.silentGain = undefined;
    this.keptContext = undefined;
    if (this.audioContext) {
      if (keepContext) {
        this.keptContext = this.audioContext;
        if (mode === 'keepContextSuspended') {
          await this.audioContext.suspend();
        }
      } else {
        await this.audioContext.close();
        this.audioContext = undefined;
        this.guideBus = undefined;
        this.workletContext = undefined;
      }
    }
    this.log('capture.stopped', { mode, results: this.resultCount, workletStats: this.workletStats });
  }

  setDetectorConfig(partial: Partial<DetectorConfig>) {
    this.detectorConfig = { ...this.detectorConfig, ...partial };
    this.postToWorker({ type: 'config', config: partial });
    if (partial.windowSize || partial.hopSize) {
      this.postToWorklet({ type: 'configure', windowSize: partial.windowSize, hopSize: partial.hopSize });
    }
  }

  setTargetFrequency(frequency: number | undefined) {
    this.setDetectorConfig({ targetFrequency: frequency });
  }

  // ---- guide playback -----------------------------------------------------------------------

  async playGuide(options: GuideOptions, durationSec?: number): Promise<void> {
    this.stopGuide();
    const context = this.ensureContext();
    await context.resume();
    const when = context.currentTime + 0.02;
    const frequency = midiToFrequency(options.midi);
    if (options.kind === 'piano') {
      if (!(await loadInstrument())) {
        return; // the soundfont could not be loaded; there is no piano note to play
      }
      const note = this.playPianoNote(options.midi, options.gain, when);
      this.activeGuide = { options, stop: (at) => stopNote(note, at) };
    } else {
      const oscillator = context.createOscillator();
      oscillator.type = options.kind;
      oscillator.frequency.value = frequency;
      const gain = context.createGain();
      gain.gain.setValueAtTime(0, when);
      gain.gain.linearRampToValueAtTime(options.gain, when + 0.02);
      oscillator.connect(gain);
      gain.connect(this.guideOutput());
      oscillator.start(when);
      this.activeGuide = {
        options,
        stop: (at) => {
          gain.gain.cancelScheduledValues(at);
          gain.gain.setTargetAtTime(0, at, 0.02);
          oscillator.stop(at + 0.2);
        },
      };
    }
    this.log('guide.play', { ...options, frequency, durationSec });
    if (durationSec) {
      this.guideTimer = setTimeout(() => this.stopGuide(), durationSec * 1000);
    }
  }

  stopGuide() {
    if (this.guideTimer) {
      clearTimeout(this.guideTimer);
      this.guideTimer = undefined;
    }
    if (this.activeGuide && this.audioContext) {
      this.activeGuide.stop(this.audioContext.currentTime);
      this.log('guide.stop', this.activeGuide.options);
    }
    this.activeGuide = undefined;
  }

  // ---- measurements -------------------------------------------------------------------------

  // Throughput and latency over the last `seconds` of frames: what the phone actually sustains
  recentStats(seconds: number) {
    const context = this.audioContext;
    if (!context) {
      return undefined;
    }
    const now = context.currentTime;
    const frames = this.resultsBetween(now - seconds, now);
    const analysed = frames.filter((frame) => frame.computeMs > 0);
    const voiced = frames.filter((frame) => frame.yin && frame.yin.clarity >= voicedClarity && frame.dbfs > voicedDbfs);
    return {
      seconds,
      frames: frames.length,
      framesPerSecond: frames.length / seconds,
      computeMsMedian: median(analysed.map((frame) => frame.computeMs)),
      yinMsMedian: median(analysed.filter((frame) => frame.yinMs !== undefined).map((frame) => frame.yinMs!)),
      mpmMsMedian: median(analysed.filter((frame) => frame.mpmMs !== undefined).map((frame) => frame.mpmMs!)),
      targetMsMedian: median(analysed.filter((frame) => frame.targetMs !== undefined).map((frame) => frame.targetMs!)),
      computeMsMax: analysed.length ? Math.max(...analysed.map((frame) => frame.computeMs)) : undefined,
      mainThreadLatencyMsMedian: median(frames.map((frame) => frame.mainThreadLatencyMs ?? 0)),
      mainThreadLatencyMsMax: frames.length
        ? Math.max(...frames.map((frame) => frame.mainThreadLatencyMs ?? 0))
        : undefined,
      dbfsMedian: median(frames.map((frame) => frame.dbfs).filter((value) => Number.isFinite(value))),
      voicedRate: frames.length ? voiced.length / frames.length : 0,
      workletStats: this.workletStats,
      // the own canceller's delay, levels, share of frames cleaned and time per frame, when it runs
      canceller: echoSummary(frames),
    };
  }

  resultsBetween(fromTime: number, toTime: number): FrameResult[] {
    // results are time-ordered; walk back from the end so recent windows cost O(window), not O(history)
    const selected: FrameResult[] = [];
    for (let i = this.results.length - 1; i >= 0; i -= 1) {
      const result = this.results[i];
      if (result.time < fromTime) {
        break;
      }
      if (result.time <= toTime) {
        selected.push(result);
      }
    }
    return selected.reverse();
  }

  private recentNoiseRms(seconds: number): number {
    const context = this.ensureContext();
    const now = context.currentTime;
    const values = this.resultsBetween(now - seconds, now).map((result) => result.rms);
    return median(values) ?? 0;
  }

  // Emit short clicks through the speaker and time their arrival at the mic on the audio clock.
  // Meaningful with echo cancellation off; with it on, the click may be cancelled or delayed.
  async measureRoundTrip(count = 5, onProgress?: (done: number) => void): Promise<RoundTripResult> {
    const context = this.ensureContext();
    if (!this.running) {
      throw new Error('start the mic first');
    }
    const samplesMs: number[] = [];
    const clicks: Array<{ floorRms: number; quantumRms: number; peak: number }> = [];
    let timeouts = 0;
    await sleep(600); // let the worklet's floor tracker settle
    const threshold = 0.0003;
    for (let i = 0; i < count; i += 1) {
      const scheduled = context.currentTime + 0.25;
      const arrival = new Promise<number | undefined>((resolve) => {
        this.clickResolver = resolve;
        setTimeout(() => {
          if (this.clickResolver === resolve) {
            this.clickResolver = undefined;
            resolve(undefined);
          }
        }, 1200);
      });
      this.lastClick = undefined;
      this.postToWorklet({ type: 'armClick', minRms: threshold });
      const oscillator = context.createOscillator();
      oscillator.frequency.value = 1000;
      const gain = context.createGain();
      gain.gain.setValueAtTime(1, scheduled);
      gain.gain.setValueAtTime(0, scheduled + 0.01);
      oscillator.connect(gain);
      gain.connect(this.guideOutput());
      oscillator.start(scheduled);
      oscillator.stop(scheduled + 0.02);
      const heardAt = await arrival;
      if (heardAt === undefined) {
        timeouts += 1;
      } else {
        samplesMs.push((heardAt - scheduled) * 1000);
        if (this.lastClick) {
          clicks.push(this.lastClick);
        }
      }
      onProgress?.(i + 1);
      await sleep(500);
    }
    const result: RoundTripResult = { samplesMs, medianMs: median(samplesMs), threshold, timeouts };
    this.log('measure.roundTrip', { ...result, clicks, mic: this.micOptions, context: this.getContextInfo() });
    return result;
  }

  // Play each stimulus through the speaker while the mic listens and report how much of it reaches
  // the detectors. Run once with echo cancellation on and once with it off to get the depth.
  async measureLeak(stimuli: LeakStimulus[], onProgress?: (label: string) => void): Promise<LeakResult[]> {
    const context = this.ensureContext();
    if (!this.running) {
      throw new Error('start the mic first');
    }
    const previousTarget = this.detectorConfig.targetFrequency;
    const results: LeakResult[] = [];
    for (const stimulus of stimuli) {
      onProgress?.(stimulus.label);
      this.stopGuide();
      await sleep(800);
      const noiseRms = this.recentNoiseRms(0.5);
      const frequency = midiToFrequency(stimulus.midi);
      this.setTargetFrequency(frequency);
      const startedAt = context.currentTime;
      await this.playGuide(stimulus, 2.5);
      await sleep(2700);
      const frames = this.resultsBetween(startedAt + 0.8, startedAt + 2.3);
      const levelRms = median(frames.map((frame) => frame.rms)) ?? 0;
      const locked = frames.filter(
        (frame) => frame.yin && frame.yin.clarity > 0.5 && Math.abs(centsBetween(frame.yin.frequency, frequency)) < 50,
      );
      const result: LeakResult = {
        label: stimulus.label,
        frequency,
        noiseDbfs: toDbfs(noiseRms),
        levelDbfs: toDbfs(levelRms),
        leakDb: noiseRms > 0 ? 20 * Math.log10(levelRms / noiseRms) : Infinity,
        yinLockRate: frames.length ? locked.length / frames.length : 0,
        targetClarity: median(frames.filter((frame) => frame.target).map((frame) => frame.target!.clarity)),
        frames: frames.length,
        trace: traceFrames(frames),
      };
      results.push(result);
    }
    this.setTargetFrequency(previousTarget);
    this.log('measure.leak', { results, mic: this.micOptions, context: this.getContextInfo() });
    return results;
  }

  // Guided protocol: the singer holds one note while the guide is silent, plays a different note,
  // plays the same note, and finally plays alone. Reports whether the detector stays on the singer.
  // Every phase carries its span on the audio clock and the window its rates were scored over.
  //
  // With `record`, the run records itself (issue #3708): microphone and guide reference from before
  // the first instruction to the end of the last phase, uploaded as a two-channel WAV whose sidecar
  // holds the result and every phase as sample offsets into the WAV (lab_recording.ts), so the
  // offline canceller tests can cut and score the take without the session log. The log entry then
  // names the WAV. An aborted or failed run's recording is discarded, and so is one whose mic was
  // stopped under it (stop() ends the recording; the page's unmount calls it): the run ends at its
  // next second and uploads nothing.
  async runDoubleTalk(
    guide: Pick<GuideOptions, 'kind' | 'gain'>,
    onPhase: (instruction: string, secondsLeft: number) => void,
    record?: DoubleTalkRecordOptions,
  ): Promise<DoubleTalkResult> {
    if (!this.running) {
      throw new Error('start the mic first');
    }
    const generation = this.captureGeneration;
    const micOpen = () => this.running && this.captureGeneration === generation;
    if (!record) {
      const result = await this.doubleTalkPhases(guide, onPhase, micOpen);
      this.logDoubleTalk(result, guide);
      return result;
    }
    if (this.recording) {
      throw new Error('a recording is already running');
    }
    this.startRecording();
    await sleep(100); // the worklet starts on its next render quantum; let it cover the first phase's start
    let result: DoubleTalkResult;
    try {
      result = await this.doubleTalkPhases(guide, onPhase, micOpen);
    } catch (error) {
      if (this.recording) {
        await this.discardRecording();
      }
      throw error;
    }
    // Asked before stopping it: a recording stop() already ended is not this run's to upload
    const survived = this.recording && micOpen();
    const recording = this.recording ? await this.stopRecording() : undefined;
    const verdict = recordedRunVerdict({
      aborted: result.aborted,
      survived,
      samples: recording?.samples.length ?? 0,
    });
    if (!verdict.upload || !recording) {
      result.recordingError = verdict.error;
      this.log('record.discarded', { reason: verdict.error ?? result.aborted });
      this.logDoubleTalk(result, guide);
      return result;
    }
    const label = doubleTalkRecordingLabel(record.label, guide);
    const metadata = doubleTalkRecordingMetadata(recording, result, {
      label,
      guide,
      mic: this.micOptions,
      context: this.getContextInfo(),
      track: this.trackSettings,
      detector: this.detectorConfig,
      userAgent: navigator.userAgent,
    });
    onPhase('Uploading the recording', 0);
    try {
      result.recording = await this.uploadRecording(label, recording, metadata);
      onPhase(`Done; saved ${result.recording}`, 0);
    } catch (error) {
      result.recordingError = (error as Error).message ?? String(error);
      onPhase(`Done, but the recording did not upload: ${result.recordingError}`, 0);
    }
    this.logDoubleTalk(result, guide);
    return result;
  }

  // An aborted run logs its result alone, as it always has
  private logDoubleTalk(result: DoubleTalkResult, guide: Pick<GuideOptions, 'kind' | 'gain'>) {
    this.log('measure.doubleTalk', result.aborted ? result : { ...result, guide, mic: this.micOptions });
  }

  // `micOpen` turns false once the mic this run started on has stopped; each countdown then gives up
  // at its next second and the run ends as aborted, playing nothing more
  private async doubleTalkPhases(
    guide: Pick<GuideOptions, 'kind' | 'gain'>,
    onPhase: (instruction: string, secondsLeft: number) => void,
    micOpen: () => boolean,
  ): Promise<DoubleTalkResult> {
    const context = this.ensureContext();
    const previousTarget = this.detectorConfig.targetFrequency;
    const phases: DoubleTalkPhase[] = [];
    const countdown = async (instruction: string, seconds: number): Promise<boolean> => {
      for (let left = seconds; left > 0; left -= 1) {
        onPhase(instruction, left);
        await sleep(1000);
        if (!micOpen()) {
          return false;
        }
      }
      return true;
    };
    const micStopped = (): DoubleTalkResult => {
      this.stopGuide();
      this.setTargetFrequency(previousTarget);
      onPhase('The mic stopped; the run was abandoned', 0);
      return { phases, aborted: micStoppedReason };
    };
    const isVoiced = (frame: FrameResult) =>
      Boolean(frame.yin && frame.yin.clarity >= voicedClarity && frame.dbfs > voicedDbfs);
    const within = (frame: FrameResult, hz: number) =>
      Boolean(frame.yin && Math.abs(centsBetween(frame.yin.frequency, hz)) < 50);
    const summarize = (
      label: string,
      span: { startSec: number; endSec: number },
      from: number,
      to: number,
      singerHz?: number,
      guideMidi?: number,
    ) => {
      const frames = this.resultsBetween(from, to);
      const voiced = frames.filter(isVoiced);
      const phase: DoubleTalkPhase = {
        label,
        guideMidi,
        ...span,
        scoredFromSec: from,
        scoredToSec: to,
        frames: frames.length,
        voicedRate: frames.length ? voiced.length / frames.length : 0,
        trace: traceFrames(frames),
      };
      if (singerHz) {
        phase.onSingerRate = frames.length
          ? frames.filter((frame) => within(frame, singerHz)).length / frames.length
          : 0;
        const targeted = frames.filter((frame) => frame.target);
        phase.targetOnSingerRate = frames.length
          ? targeted.filter((frame) => Math.abs(frame.target!.cents) < 50 && frame.target!.clarity >= 0.4).length /
            frames.length
          : 0;
        phase.targetClarityMedian = median(targeted.map((frame) => frame.target!.clarity));
      }
      const echo = echoSummary(frames);
      if (echo) {
        phase.micDbfsMedian = echo.micDbfsMedian;
        phase.echoDbfsMedian = echo.echoDbfsMedian;
        phase.residualDbfsMedian = echo.residualDbfsMedian;
        phase.delayMsMedian = echo.delayMsMedian;
        phase.cancelledRate = echo.cancelledRate;
      }
      if (guideMidi !== undefined) {
        const guideHz = midiToFrequency(guideMidi);
        phase.onGuideRate = frames.length ? frames.filter((frame) => within(frame, guideHz)).length / frames.length : 0;
      }
      phases.push(phase);
      return { frames, voiced };
    };

    this.stopGuide();
    this.setTargetFrequency(undefined);
    let start = context.currentTime;
    if (!(await countdown('Sing and hold one comfortable note ("ahh")', 5))) {
      return micStopped();
    }
    let end = context.currentTime;
    const alone = summarize('singer alone', { startSec: start, endSec: end }, end - 3, end);
    const singerHz = median(alone.voiced.map((frame) => frame.yin!.frequency));
    if (!singerHz || alone.voiced.length < alone.frames.length * 0.3) {
      onPhase('Could not hear a steady note. Try again, louder.', 0);
      return { phases, aborted: 'could not hear a steady note; sing louder or closer' };
    }
    const singerMidi = Math.round(frequencyToMidi(singerHz));
    const otherMidi = singerMidi + 7 <= 84 ? singerMidi + 7 : singerMidi - 5;
    this.setTargetFrequency(singerHz); // known-target estimator follows the singer through the guide phases

    await this.playGuide({ ...guide, midi: otherMidi });
    start = context.currentTime;
    if (!(await countdown(`Keep holding your note while the guide plays ${midiToNoteName(otherMidi)}`, 4))) {
      return micStopped();
    }
    end = context.currentTime;
    summarize('guide on a different note', { startSec: start, endSec: end }, start + 0.5, end, singerHz, otherMidi);

    await this.playGuide({ ...guide, midi: singerMidi });
    start = context.currentTime;
    if (!(await countdown(`Keep holding; the guide now plays your note ${midiToNoteName(singerMidi)}`, 4))) {
      return micStopped();
    }
    end = context.currentTime;
    summarize('guide on the same note', { startSec: start, endSec: end }, start + 0.5, end, singerHz, singerMidi);

    await this.playGuide({ ...guide, midi: otherMidi });
    start = context.currentTime;
    if (!(await countdown('Stop singing; the guide plays alone', 4))) {
      return micStopped();
    }
    end = context.currentTime;
    summarize('guide alone', { startSec: start, endSec: end }, start + 1.5, end, singerHz, otherMidi);
    this.stopGuide();
    this.setTargetFrequency(previousTarget);
    onPhase('Done', 0);

    return { singerHz, singerMidi, otherMidi, phases };
  }

  // ---- recording ----------------------------------------------------------------------------

  // The worklet streams the microphone and the guide reference back in chunks while recording is on
  startRecording() {
    if (!this.running) {
      throw new Error('start the mic first');
    }
    this.recordChunks = [];
    this.recording = true;
    this.postToWorklet({ type: 'record', enabled: true });
    this.log('record.start');
  }

  async stopRecording(): Promise<LabRecording> {
    this.postToWorklet({ type: 'record', enabled: false });
    this.recording = false;
    await sleep(150); // let the final chunk arrive
    const { samples, reference, startTimeSec } = joinRecordedChunks(this.recordChunks);
    this.recordChunks = [];
    const sampleRate = this.audioContext?.sampleRate ?? this.detectorConfig.sampleRate;
    this.log('record.stop', { samples: samples.length, seconds: samples.length / sampleRate, startTimeSec });
    return { samples, reference, sampleRate, startTimeSec };
  }

  private async discardRecording() {
    await this.stopRecording();
    this.log('record.discarded', { reason: 'the run failed' });
  }

  // A two-channel WAV, microphone then guide reference. The sidecar is `metadata` plus the
  // recording's sample rate, channels and start on the audio clock (lab_recording.ts); it travels in
  // the query string, so it is kept to numbers and short strings.
  async uploadRecording(label: string, recording: LabRecording, metadata: object): Promise<string> {
    const { samples, reference, sampleRate, startTimeSec } = recording;
    const wav = encodeWav([samples, reference], sampleRate); // in the order recordingChannels names them
    const query = new URLSearchParams({
      label,
      metadata: JSON.stringify(recordingMetadata(recording, metadata)),
    });
    const response = await fetch(`/api/dev/audio-lab/recording?${query.toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'audio/wav' },
      body: wav,
    });
    if (!response.ok) {
      throw new Error(`upload failed: ${response.status}`);
    }
    const { path } = (await response.json()) as { path: string };
    this.log('record.uploaded', { label, path, sampleRate, samples: samples.length, channels: 2, startTimeSec });
    return path;
  }

  async uploadSession(): Promise<string> {
    const response = await fetch('/api/dev/audio-lab/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.session),
    });
    if (!response.ok) {
      throw new Error(`upload failed: ${response.status}`);
    }
    const { path } = (await response.json()) as { path: string };
    return path;
  }
}

export interface IosAudioSessionOverride {
  port?: 'speaker' | 'none';
  category?: 'playAndRecord' | 'playback';
  mode?: 'default' | 'voiceChat' | 'videoChat';
  defaultToSpeaker?: boolean;
  allowBluetooth?: boolean;
  active?: boolean; // setActive on the host session; false also notifies other sessions
}

// True inside the iOS wrapper. The buttons show whenever the wrapper is present so an older build
// without the handler reports a clear error on tap instead of silently hiding them.
export function hasIosAudioOverride(): boolean {
  return Boolean(window.webkit?.messageHandlers);
}

// iOS wrapper only: ask the host app to change the AVAudioSession route or category and return
// the resulting session snapshot (see setAudioSessionOverride in the wrapper's WebView.swift)
export async function setIosAudioSessionOverride(override: IosAudioSessionOverride): Promise<unknown> {
  const handler = window.webkit?.messageHandlers?.setAudioSessionOverride;
  if (!handler) {
    throw new Error(
      window.webkit?.messageHandlers
        ? 'this wrapper build has no setAudioSessionOverride handler'
        : 'not running inside the iOS wrapper',
    );
  }
  return { platform: 'ios-wrapper', override, ...(await handler.postMessage(override)) };
}

// Native audio-session details from the store app wrappers, when the page runs inside one
export async function getNativeAudioInfo(): Promise<unknown> {
  const ios = window.webkit?.messageHandlers?.getAudioSessionInfo;
  if (ios) {
    try {
      return { platform: 'ios-wrapper', ...(await ios.postMessage({})) };
    } catch (error) {
      return { platform: 'ios-wrapper', error: String(error) };
    }
  }
  const android = (window as unknown as { SypNative?: { getAudioInfo?: () => string } }).SypNative;
  if (android?.getAudioInfo) {
    try {
      return { platform: 'android-wrapper', ...JSON.parse(android.getAudioInfo()) };
    } catch (error) {
      return { platform: 'android-wrapper', error: String(error) };
    }
  }
  return undefined;
}
